import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrderStats, getAllOrders } from '../../services/orders';
import { getSessions } from '../../services/sessions';
import Loading from '../../components/common/Loading';
import { formatCents, formatDateForInput } from '../../utils/formatters';
import { STATUS_COLORS, STATUS_LABELS } from '../../utils/orderConstants';
import './EmployeePage.css';
import './Dashboard.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

function startOfYear(d) {
  return new Date(d.getFullYear(), 0, 1);
}

function endOfYear(d) {
  // Last day of December — use last-ms-of-Dec-31 approach via Jan 0 trick
  return new Date(d.getFullYear(), 12, 0); // month 12, day 0 = Dec 31
}

const PRESET_RANGES = [
  { label: 'This Year',  key: 'this_year' },
  { label: 'Last Year',  key: 'last_year' },
  { label: 'Last 90d',   key: 'last_90' },
  { label: 'Last 30d',   key: 'last_30' },
  { label: 'All Time',   key: 'all' },
];

// Fix #10: compute once, destructure both values from single call
function presetToDates(key) {
  const now = new Date();
  switch (key) {
    case 'this_year': return { from: formatDateForInput(startOfYear(now)), to: formatDateForInput(endOfYear(now)) };
    case 'last_year': {
      const prev = new Date(now.getFullYear() - 1, 0, 1);
      return { from: formatDateForInput(startOfYear(prev)), to: formatDateForInput(endOfYear(prev)) };
    }
    case 'last_90': {
      const d = new Date(now); d.setDate(d.getDate() - 90);
      return { from: formatDateForInput(d), to: formatDateForInput(now) };
    }
    case 'last_30': {
      const d = new Date(now); d.setDate(d.getDate() - 30);
      return { from: formatDateForInput(d), to: formatDateForInput(now) };
    }
    case 'all':
    default:
      return { from: '', to: '' };
  }
}

// Fix #10: compute initial dates once — not twice in separate useState calls
const INITIAL_DATES = presetToDates('this_year');

// ── Quick links ───────────────────────────────────────────────────────────────

const QUICK_LINKS = [
  {
    label: 'Orders',
    path: '/employee/orders',
    description: 'View and manage all orders',
    icon: (
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <rect x="3" y="2" width="14" height="16" rx="1" />
        <line x1="7" y1="7" x2="13" y2="7" />
        <line x1="7" y1="10" x2="13" y2="10" />
        <line x1="7" y1="13" x2="10" y2="13" />
      </svg>
    ),
  },
  {
    label: 'Customers',
    path: '/employee/customers',
    description: 'Browse companies and contacts',
    icon: (
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <circle cx="10" cy="6" r="3" />
        <path d="M3 17c0-3.314 3.134-6 7-6s7 2.686 7 6" />
      </svg>
    ),
  },
  {
    label: 'Products',
    path: '/employee/products',
    description: 'Manage products and variants',
    icon: (
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M10 2L3 6v8l7 4 7-4V6l-7-4z" />
        <line x1="10" y1="2" x2="10" y2="18" />
        <line x1="3" y1="6" x2="17" y2="6" />
      </svg>
    ),
  },
  {
    label: 'Growers',
    path: '/employee/growers',
    description: 'View farm and grower records',
    icon: (
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M10 17V9" />
        <path d="M10 9C10 9 6 8 5 4c2 0 4 1 5 5z" />
        <path d="M10 9C10 9 14 8 15 4c-2 0-4 1-5 5z" />
        <line x1="4" y1="17" x2="16" y2="17" />
      </svg>
    ),
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

const RECENT_LIMIT = 8;

const EmployeeDashboard = () => {
  const navigate = useNavigate();

  // Fix #10: consume pre-computed INITIAL_DATES — presetToDates called only once
  const [preset, setPreset]       = useState('this_year');
  const [dateFrom, setDateFrom]   = useState(INITIAL_DATES.from);
  const [dateTo, setDateTo]       = useState(INITIAL_DATES.to);
  const [sessionFilter, setSessionFilter] = useState('');

  // Sessions for the filter dropdown
  const [sessions, setSessions] = useState([]);
  useEffect(() => {
    getSessions({ employee: true })
      .then((data) => setSessions(data))
      .catch(() => setSessions([]));
  }, []);

  // Stats from the dedicated aggregate endpoint
  const [stats, setStats]               = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError]     = useState(null);

  // Fix #5: fetch only the 8 most-recent orders directly; don't load all 20 just to slice
  const [recentOrders, setRecentOrders]         = useState([]);
  const [recentLoading, setRecentLoading]       = useState(true);

  // ── Fetch stats ──────────────────────────────────────────────────
  const fetchStats = useCallback(async (from, to, session) => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const data = await getOrderStats({ dateFrom: from, dateTo: to, session });
      setStats(data);
    } catch (err) {
      // Fix #11: surface the actual error message instead of discarding it
      setStatsError(err?.message || 'Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Fix #5: fetch recent orders with page_size=8 to avoid over-fetching
  const fetchRecent = useCallback(async (session) => {
    setRecentLoading(true);
    try {
      const data = await getAllOrders({ page_size: RECENT_LIMIT, session });
      setRecentOrders(data.results || []);
    } catch {
      // Best-effort — don't surface a blocking error for the recent list
    } finally {
      setRecentLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(dateFrom, dateTo, sessionFilter);
  }, [dateFrom, dateTo, sessionFilter, fetchStats]);

  useEffect(() => {
    fetchRecent(sessionFilter);
  }, [sessionFilter, fetchRecent]);

  // ── Preset picker ────────────────────────────────────────────────
  const handlePreset = (key) => {
    setPreset(key);
    const { from, to } = presetToDates(key);
    setDateFrom(from);
    setDateTo(to);
  };

  const handleDateChange = (field, value) => {
    setPreset('custom');
    if (field === 'from') setDateFrom(value);
    else setDateTo(value);
  };

  // ── Monthly chart scale ──────────────────────────────────────────
  const maxMonthRevenue = useMemo(() => {
    if (!stats?.by_month?.length) return 1;
    return Math.max(...stats.by_month.map((m) => m.revenue_cents));
  }, [stats]);

  const maxMonthOrders = useMemo(() => {
    if (!stats?.by_month?.length) return 1;
    return Math.max(...stats.by_month.map((m) => m.order_count));
  }, [stats]);

  // ── Status breakdown scale ───────────────────────────────────────
  const maxStatusCount = useMemo(() => {
    if (!stats?.by_status) return 1;
    return Math.max(...Object.values(stats.by_status).map((s) => s.count));
  }, [stats]);

  // ── Product breakdown scale ──────────────────────────────────────
  const maxProductRevenue = useMemo(() => {
    if (!stats?.by_product?.length) return 1;
    return Math.max(...stats.by_product.map((p) => p.revenue_cents));
  }, [stats]);

  // ── Navigate handler for keyboard-accessible table rows ──────────
  // Fix #8: <tr> has implicit role "row" — no role="button" needed.
  // Instead handle both Enter AND Space for keyboard activation.
  const handleRowKey = useCallback((e, path) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(path);
    }
  }, [navigate]);

  return (
    <div className="employee-page dash-page">

      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="employee-page__header dash-header">
        <div>
          <p className="employee-page__eyebrow">Employee Portal</p>
          <h1 className="employee-page__title">Dashboard</h1>
        </div>

        {/* Date range controls */}
        <div className="dash-date-controls">
          <div className="dash-presets">
            {PRESET_RANGES.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`dash-preset${preset === p.key ? ' dash-preset--active' : ''}`}
                onClick={() => handlePreset(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="dash-date-inputs">
            <input
              type="date"
              className="dash-date-input"
              value={dateFrom}
              onChange={(e) => handleDateChange('from', e.target.value)}
              aria-label="From date"
            />
            <span className="dash-date-sep">–</span>
            <input
              type="date"
              className="dash-date-input"
              value={dateTo}
              onChange={(e) => handleDateChange('to', e.target.value)}
              aria-label="To date"
            />
          </div>
          {sessions.length > 0 && (
            <div className="dash-session-filter">
              <label className="dash-session-label" htmlFor="dash-session-filter">Session</label>
              <select
                id="dash-session-filter"
                className="dash-date-input"
                value={sessionFilter}
                onChange={(e) => setSessionFilter(e.target.value)}
                aria-label="Filter by mill session"
              >
                <option value="">All Sessions</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* ── Stat cards ───────────────────────────────────────────── */}
      {statsLoading ? (
        <div className="dash-stats-loading"><Loading /></div>
      ) : statsError ? (
        <p className="dash-error">{statsError}</p>
      ) : stats ? (
        <div className="dash-stats">
          <div className="dash-stat">
            <span className="dash-stat__value">{stats.total_orders}</span>
            <span className="dash-stat__label">Total Orders</span>
          </div>
          {/* Fix #7: formatCents replaces inline / 100 */}
          <div className="dash-stat dash-stat--highlight">
            <span className="dash-stat__value">{formatCents(stats.total_revenue_cents)}</span>
            <span className="dash-stat__label">Revenue</span>
          </div>
          <div className="dash-stat">
            <span className="dash-stat__value">
              {(stats.by_status?.pending?.count || 0)}
            </span>
            <span className="dash-stat__label">Pending</span>
          </div>
          <div className="dash-stat">
            <span className="dash-stat__value">
              {(stats.by_status?.delivered?.count || 0)}
            </span>
            <span className="dash-stat__label">Delivered</span>
          </div>
          <div className="dash-stat">
            <span className="dash-stat__value">
              {(stats.by_status?.cancelled?.count || 0)}
            </span>
            <span className="dash-stat__label">Cancelled</span>
          </div>
        </div>
      ) : null}

      {/* ── Monthly chart ─────────────────────────────────────────── */}
      {!statsLoading && stats?.by_month?.length > 0 && (
        <section className="dash-card dash-monthly">
          <div className="dash-card__head">
            <h2 className="dash-card__title">Monthly Breakdown</h2>
          </div>
          <div className="dash-chart">
            {stats.by_month.map((m) => {
              const BAR_MAX_PX = 120;
              const revPx    = maxMonthRevenue > 0 ? Math.max(3, (m.revenue_cents / maxMonthRevenue) * BAR_MAX_PX) : 3;
              const ordersPx = maxMonthOrders  > 0 ? Math.max(3, (m.order_count  / maxMonthOrders)  * BAR_MAX_PX) : 3;
              return (
                <div key={m.month} className="dash-chart__col">
                  <div className="dash-chart__bars">
                    <div
                      className="dash-chart__bar dash-chart__bar--rev"
                      style={{ height: `${revPx}px` }}
                      title={`Revenue: ${formatCents(m.revenue_cents)}`}
                    />
                    <div
                      className="dash-chart__bar dash-chart__bar--orders"
                      style={{ height: `${ordersPx}px` }}
                      title={`Orders: ${m.order_count}`}
                    />
                  </div>
                  <span className="dash-chart__label">{m.label}</span>
                  {/* Fix #7: formatCents throughout */}
                  <span className="dash-chart__rev">{formatCents(m.revenue_cents)}</span>
                  <span className="dash-chart__count">{m.order_count} orders</span>
                </div>
              );
            })}
          </div>
          <div className="dash-chart__legend">
            <span className="dash-chart__legend-item dash-chart__legend-item--rev">Revenue</span>
            <span className="dash-chart__legend-item dash-chart__legend-item--orders">Order Count</span>
          </div>
        </section>
      )}

      {/* ── Lower grid ────────────────────────────────────────────── */}
      <div className="dash-grid">

        {/* ── Product breakdown ─────────────────────────────────── */}
        {!statsLoading && stats?.by_product?.length > 0 && (
          <section className="dash-card dash-card--products">
            <div className="dash-card__head">
              <h2 className="dash-card__title">Product Breakdown</h2>
              <button className="dash-print-btn" onClick={() => window.print()} aria-label="Print product breakdown">
                Print
              </button>
            </div>
            <div className="dash-print-header">
              <strong>Deep Roots Milling — Product Breakdown</strong>
              <span>
                {sessionFilter
                  ? (sessions.find((s) => String(s.id) === String(sessionFilter))?.label ?? 'Session')
                  : (PRESET_RANGES.find((p) => p.key === preset)?.label ?? `${dateFrom || ''}${dateFrom && dateTo ? ' – ' : ''}${dateTo || ''}`)}
              </span>
            </div>
            <div className="dash-products">
              {stats.by_product.map((p) => {
                const pct = maxProductRevenue > 0 ? (p.revenue_cents / maxProductRevenue) * 100 : 0;
                return (
                  <div key={p.product_id} className="dash-product">
                    <div className="dash-product__head">
                      <span className="dash-product__name">{p.product_name}</span>
                      {/* Fix #7: formatCents */}
                      <span className="dash-product__rev">{formatCents(p.revenue_cents)}</span>
                    </div>
                    <div className="dash-product__bar-track">
                      <div className="dash-product__bar" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="dash-product__meta">
                      <span className="dash-product__qty">{p.quantity.toLocaleString()} units</span>
                      {p.total_weight_lbs > 0 && (
                        <span className="dash-product__weight">{p.total_weight_lbs.toLocaleString()} lbs</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Right column ──────────────────────────────────────── */}
        <div className="dash-right">

          {/* Status breakdown */}
          {!statsLoading && stats?.by_status && Object.keys(stats.by_status).length > 0 && (
            <section className="dash-card dash-card--breakdown">
              <div className="dash-card__head">
                <h2 className="dash-card__title">By Status</h2>
              </div>
              <div className="dash-breakdown">
                {Object.entries(stats.by_status)
                  .sort(([, a], [, b]) => b.count - a.count)
                  .map(([st, { count, revenue_cents }]) => {
                    // Fix #4: STATUS_COLORS / STATUS_LABELS are the single source of truth
                    const color = STATUS_COLORS[st] || '#9c8f7e';
                    const label = STATUS_LABELS[st] || st;
                    const pct   = maxStatusCount > 0 ? ((count / maxStatusCount) * 100).toFixed(0) : 0;
                    return (
                      <div key={st} className="dash-breakdown__row">
                        <span className="dash-breakdown__label" style={{ color }}>{label}</span>
                        <div className="dash-breakdown__bar-wrap">
                          <div className="dash-breakdown__bar" style={{ width: `${pct}%`, background: color }} />
                        </div>
                        <span className="dash-breakdown__count">{count}</span>
                        {/* Fix #7: formatCents */}
                        <span className="dash-breakdown__rev">{formatCents(revenue_cents)}</span>
                      </div>
                    );
                  })}
              </div>
            </section>
          )}

          {/* Quick links */}
          <section className="dash-card dash-card--links">
            <div className="dash-card__head">
              <h2 className="dash-card__title">Quick Links</h2>
            </div>
            <div className="dash-quick-links">
              {QUICK_LINKS.map((link) => (
                <button
                  key={link.path}
                  type="button"
                  className="dash-quick-link"
                  onClick={() => navigate(link.path)}
                >
                  <span className="dash-quick-link__icon">{link.icon}</span>
                  <span className="dash-quick-link__body">
                    <span className="dash-quick-link__label">{link.label}</span>
                    <span className="dash-quick-link__desc">{link.description}</span>
                  </span>
                  <span className="dash-quick-link__arrow" aria-hidden="true">›</span>
                </button>
              ))}
            </div>
          </section>

        </div>
      </div>

      {/* ── Recent orders ─────────────────────────────────────────── */}
      <section className="dash-card dash-card--orders">
        <div className="dash-card__head">
          <h2 className="dash-card__title">Recent Orders</h2>
          <button
            type="button"
            className="dash-card__link"
            onClick={() => navigate('/employee/orders')}
          >
            View all →
          </button>
        </div>

        {recentLoading ? (
          <div className="dash-loading"><Loading /></div>
        ) : recentOrders.length === 0 ? (
          <p className="dash-empty">No orders yet.</p>
        ) : (
          <table className="dash-orders-table">
            <thead>
              <tr>
                <th className="dash-orders-table__th">Order</th>
                <th className="dash-orders-table__th">Customer</th>
                <th className="dash-orders-table__th dash-orders-table__th--right">Amount</th>
                <th className="dash-orders-table__th">Status</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map((order) => {
                // Fix #4: STATUS_COLORS / STATUS_LABELS are the single source of truth
                const color = STATUS_COLORS[order.status] || '#9c8f7e';
                const label = STATUS_LABELS[order.status] || order.status;
                const path  = `/employee/orders/${order.id}`;
                return (
                  // Fix #8: remove role="button" (implicit role is "row").
                  // Add Space-key handler alongside Enter for full keyboard support.
                  <tr
                    key={order.id}
                    className="dash-orders-table__row"
                    onClick={() => navigate(path)}
                    tabIndex={0}
                    onKeyDown={(e) => handleRowKey(e, path)}
                  >
                    <td className="dash-orders-table__td dash-orders-table__td--order">
                      {order.order_number}
                    </td>
                    <td className="dash-orders-table__td dash-orders-table__td--company">
                      {order.company?.company_name || '—'}
                    </td>
                    <td className="dash-orders-table__td dash-orders-table__td--amount">
                      {order.total_amount_dollars
                        ? formatCents(Math.round(parseFloat(order.total_amount_dollars) * 100))
                        : '—'}
                    </td>
                    <td className="dash-orders-table__td">
                      <span
                        className="dash-status-badge"
                        style={{ color, borderColor: `${color}40`, background: `${color}12` }}
                      >
                        {label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

    </div>
  );
};

export default EmployeeDashboard;
